import { flexRender, type Row, type Table } from "@tanstack/react-table";
import { ColumnResizeHandle } from "./ColumnResizeHandle";
import { SortableHeader } from "./SortableHeader";

function alignClass(align: "start" | "center" | "end" | undefined): string {
  if (align === "start") return "text-start";
  if (align === "end") return "text-end";
  if (align === "center") return "text-center";
  return "text-start";
}

type DataTableProps<T> = {
  table: Table<T>;
  displayRows: T[];
  onRowClick?: (row: T) => void;
  headerRowClassName?: string;
  rowClassName?: string;
};

export function DataTable<T>({
  table,
  displayRows,
  onRowClick,
  headerRowClassName = "text-xs font-semibold uppercase tracking-wide bg-slate-100 text-slate-500",
  rowClassName = "cursor-pointer border-t border-slate-200 text-sm text-slate-700 hover:bg-slate-50",
}: DataTableProps<T>) {
  const rowByOriginal = new Map<T, Row<T>>(
    table.getRowModel().rows.map((row) => [row.original, row]),
  );

  return (
    <table className="w-full table-fixed border-collapse">
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} className={headerRowClassName}>
            {headerGroup.headers.map((header) => {
              const meta = header.column.columnDef.meta;
              const align = alignClass(meta?.align);
              return (
                <th
                  key={header.id}
                  style={{ width: header.getSize() }}
                  className={`relative px-4 py-3 ${align} ${meta?.headerClassName ?? ""}`}
                >
                  {header.isPlaceholder ? null : (
                    <SortableHeader header={header} />
                  )}
                  <ColumnResizeHandle header={header} />
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody>
        {displayRows.map((original) => {
          const row = rowByOriginal.get(original);
          if (!row) return null;

          return (
            <tr
              key={row.id}
              onClick={() => onRowClick?.(original)}
              className={rowClassName}
            >
              {row.getVisibleCells().map((cell) => {
                const meta = cell.column.columnDef.meta;
                const align = alignClass(meta?.align);
                return (
                  <td
                    key={cell.id}
                    style={{ width: cell.column.getSize() }}
                    className={`px-4 py-3 ${align} ${meta?.cellClassName ?? ""}`}
                    onClick={
                      meta?.stopRowClick
                        ? (event) => event.stopPropagation()
                        : undefined
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
